import React,{useCallback,useEffect,useState}from"react";
import{Box,CircularProgress,Typography}from"@mui/material";
import{useLocation,useNavigate}from"react-router-dom";
import PeopleAltOutlinedIcon from"@mui/icons-material/PeopleAltOutlined";
import SupervisorAccountOutlinedIcon from"@mui/icons-material/SupervisorAccountOutlined";
import Sidebar from"./AdminPanel/Sidebar";
import Topbar from"./AdminPanel/Topbar";
import Administrators from"./AdminUsers/Administrators";
import Supervisors from"./AdminUsers/Supervisors";
import AdminSmtp from"./AdminSmtp";
import AdminNotifications from"./AdminNotifications";
import AdminProfile from"./AdminProfile";
import AdminTechnicians from"./AdminTechnicians";
import AdminOrder from"./AdminOrder";
import{useAuth}from"../../context/AuthContext";
import{useAlert}from"../../context/AlertContext";

export default function AdminPanel(){
  const location=useLocation();
  const navigate=useNavigate();
  const{user,authFetch}=useAuth();
  const{showError}=useAlert();

  const[sidebarOpen,setSidebarOpen]=useState(false);
  const[supervisorCompanies,setSupervisorCompanies]=useState([]);
  const[companiesLoading,setCompaniesLoading]=useState(false);

  const role=user?.role;
  const isSuperAdmin=role==="SUPERADMIN";
  const isAdmin=role==="ADMIN";
  const isSupervisor=role==="SUPERVISOR";
  const isTechnician=role==="TECHNICIAN";

  const loadSupervisorCompanies=useCallback(async()=>{
    if(!isSupervisor)return;

    setCompaniesLoading(true);

    try{
      const response=await authFetch("/api/login/supervisor/administrators/");
      const data=await response.json();

      if(!response.ok)throw new Error(data.detail||"No se pudieron cargar los proveedores asignados.");

      setSupervisorCompanies(Array.isArray(data)?data:[]);
    }catch(error){
      setSupervisorCompanies([]);
      showError(error.message||"No se pudieron cargar los proveedores asignados.");
    }finally{
      setCompaniesLoading(false);
    }
  },[authFetch,isSupervisor,showError]);

  useEffect(()=>{
    loadSupervisorCompanies();
  },[loadSupervisorCompanies]);

  const QuickCard=({title,icon,bgcolor,color,onClick})=>(
    <Box
      onClick={onClick}
      sx={{
        width:220,
        minHeight:150,
        border:"1px solid #dce3ea",
        borderRadius:"14px",
        bgcolor:"#fff",
        cursor:"pointer",
        display:"flex",
        flexDirection:"column",
        alignItems:"center",
        justifyContent:"center",
        gap:1.5,
        transition:"border-color .2s",
        "&:hover":{borderColor:"#123f6d"},
      }}
    >
      <Box sx={{width:52,height:52,borderRadius:"50%",bgcolor,color,display:"flex",alignItems:"center",justifyContent:"center"}}>
        {icon}
      </Box>

      <Typography sx={{fontSize:16,fontWeight:700,color:"#1f2937"}}>
        {title}
      </Typography>
    </Box>
  );

  const CompanyCard=({company})=>(
    <Box
      onClick={()=>navigate(`/admin-panel/orders?administrator=${company.public_id}`)}
      translate="no"
      className="notranslate"
      sx={{
        width:240,
        minHeight:220,
        border:"1px solid #dce3ea",
        borderRadius:3,
        bgcolor:"#fff",
        display:"flex",
        flexDirection:"column",
        overflow:"hidden",
        cursor:"pointer",
        transition:"border-color .2s",
        "&:hover":{borderColor:"#123f6d"},
      }}
    >
      <Box sx={{px:2,pt:2.2,pb:1.5,textAlign:"center"}}>
        <Typography translate="no" className="notranslate" sx={{fontSize:16,fontWeight:700,color:"#182230",lineHeight:1.3}}>
          {company.company_name}
        </Typography>
      </Box>

      <Box sx={{height:120,px:2,display:"flex",alignItems:"center",justifyContent:"center"}}>
        {company.logo?(
          <Box
            component="img"
            src={company.logo}
            alt={company.company_name}
            translate="no"
            className="notranslate"
            sx={{maxWidth:"100%",maxHeight:105,objectFit:"contain"}}
          />
        ):(
          <Box sx={{width:84,height:84,borderRadius:2,bgcolor:"#f2f4f7",display:"flex",alignItems:"center",justifyContent:"center"}}>
            <Typography translate="no" className="notranslate" sx={{fontSize:30,fontWeight:700,color:"#98a2b3"}}>
              {company.company_name?.charAt(0)?.toUpperCase()||"E"}
            </Typography>
          </Box>
        )}
      </Box>

      <Box sx={{mt:"auto",px:2,py:1.8,borderTop:"1px solid #edf1f5",textAlign:"center"}}>
        <Typography translate="no" className="notranslate" sx={{fontSize:13,color:"#667085",fontWeight:600}}>
          {company.service_type||"Sin tipo de servicio"}
        </Typography>
      </Box>
    </Box>
  );

  const renderHome=()=>{
    if(isAdmin||isTechnician)return <AdminOrder/>;

    if(isSupervisor){
      return(
        <Box translate="no" className="notranslate">
          <Typography translate="no" className="notranslate" sx={{fontSize:24,fontWeight:700,color:"#182230",mb:3}}>
            Proveedores
          </Typography>

          {companiesLoading&&(
            <Box sx={{py:5,display:"flex",justifyContent:"center"}}>
              <CircularProgress size={30} sx={{color:"#123f6d"}}/>
            </Box>
          )}

          {!companiesLoading&&supervisorCompanies.length===0&&(
            <Box translate="no" className="notranslate" sx={{bgcolor:"#fff",border:"1px solid #dce3ea",borderRadius:3,p:4,textAlign:"center"}}>
              <Typography translate="no" className="notranslate" sx={{fontSize:14,color:"#667085"}}>
                No tiene proveedores asignados.
              </Typography>
            </Box>
          )}

          {!companiesLoading&&supervisorCompanies.length>0&&(
            <Box sx={{display:"flex",flexWrap:"wrap",gap:2}}>
              {supervisorCompanies.map(company=>(
                <CompanyCard key={company.public_id} company={company}/>
              ))}
            </Box>
          )}
        </Box>
      );
    }

    return(
      <Box>
        <Typography sx={{fontSize:24,fontWeight:700,color:"#182230",mb:3}}>
          Accesos Rápidos
        </Typography>

        <Box sx={{display:"flex",flexWrap:"wrap",gap:2}}>
          {isSuperAdmin&&(
            <QuickCard
              title="Administradores"
              bgcolor="#e7f6ed"
              color="#25884b"
              icon={<PeopleAltOutlinedIcon/>}
              onClick={()=>navigate("/admin-panel/administrators")}
            />
          )}

          {isSuperAdmin&&(
            <QuickCard
              title="Supervisores"
              bgcolor="#f2ecfa"
              color="#6941c6"
              icon={<SupervisorAccountOutlinedIcon/>}
              onClick={()=>navigate("/admin-panel/supervisors")}
            />
          )}
        </Box>
      </Box>
    );
  };

  const renderContent=()=>{
    if(location.pathname==="/admin-panel/administrators"){
      if(!isSuperAdmin)return renderHome();
      return <Administrators/>;
    }

    if(location.pathname==="/admin-panel/supervisors"){
      if(!isSuperAdmin)return renderHome();
      return <Supervisors/>;
    }

    if(location.pathname==="/admin-panel/technicians"){
      if(!isAdmin)return renderHome();
      return <AdminTechnicians/>;
    }

    if(location.pathname.startsWith("/admin-panel/orders")){
      const isOrderList=location.pathname==="/admin-panel/orders";
      const isNewOrder=location.pathname==="/admin-panel/orders/new";
      const isOrderDetail=/^\/admin-panel\/orders\/[a-z0-9-]+\/[0-9a-fA-F-]{36}$/.test(location.pathname);

      if(!isOrderList&&!isNewOrder&&!isOrderDetail)return renderHome();

      if(isAdmin||isTechnician||isSuperAdmin)return <AdminOrder/>;

      if(isSupervisor){
        const params=new URLSearchParams(location.search);
        const administratorPublicId=params.get("administrator");

        if(isOrderList&&!administratorPublicId)return renderHome();
        if(isNewOrder)return renderHome();

        return <AdminOrder administratorId={administratorPublicId}/>;
      }

      return renderHome();
    }

    if(location.pathname==="/admin-panel/configuration/profile"){
      if(!isAdmin)return renderHome();
      return <AdminProfile/>;
    }

    if(location.pathname==="/admin-panel/configuration/smtp"){
      if(!isSuperAdmin&&!isAdmin)return renderHome();
      return <AdminSmtp/>;
    }

    if(location.pathname==="/admin-panel/configuration/notifications"){
      if(!isAdmin)return renderHome();
      return <AdminNotifications/>;
    }

    return renderHome();
  };

  return(
    <Box sx={{minHeight:"100vh",bgcolor:"#f5f7fa",display:"flex"}}>
      <Sidebar open={sidebarOpen}/>

      <Box sx={{flex:1,minWidth:0}}>
        <Topbar
          sidebarOpen={sidebarOpen}
          onToggleSidebar={()=>setSidebarOpen(prev=>!prev)}
        />

        <Box sx={{p:{xs:2,md:3}}}>
          {renderContent()}
        </Box>
      </Box>
    </Box>
  );
}